import { useDragLayer } from "react-dnd";
import { Badge } from "@/components/ui/badge";
import { Users } from "lucide-react";
import { RESERVATION_DRAG_TYPE } from "./FloorPlanDndProvider";
import { Reservation } from "@/types/table";
import { getSlotColor } from "@/lib/slotColors";

interface Props {
  /** Lookup needed because the drag item only carries the id. */
  reservationsById: Map<string, Reservation>;
}

/**
 * Floating preview that follows the touch / pointer during a drag.
 * HTML5Backend natively shows the dragged DOM; TouchBackend does not,
 * so we render this layer above everything else with pointer-events: none.
 */
export const ReservationDragLayer = ({ reservationsById }: Props) => {
  const { item, isDragging, currentOffset, itemType } = useDragLayer((monitor) => ({
    item: monitor.getItem() as { reservationId?: string } | null,
    itemType: monitor.getItemType(),
    isDragging: monitor.isDragging(),
    currentOffset: monitor.getSourceClientOffset(),
  }));

  if (!isDragging || itemType !== RESERVATION_DRAG_TYPE || !currentOffset || !item?.reservationId) {
    return null;
  }
  const r = reservationsById.get(item.reservationId);
  if (!r) return null;

  return (
    <div
      className="pointer-events-none fixed inset-0 z-[9999]"
      aria-hidden="true"
    >
      <div
        className="absolute rounded-md border-2 border-primary bg-background shadow-xl px-2 py-1.5 min-w-[160px]"
        style={{
          transform: `translate(${currentOffset.x}px, ${currentOffset.y}px) rotate(-2deg)`,
          opacity: 0.92,
        }}
      >
        <div className="flex items-center gap-1.5 text-xs font-medium">
          <Badge
            variant="outline"
            className="text-[10px] px-1.5 py-0 h-4 font-mono gap-1"
            style={{ borderColor: getSlotColor(r.startTime.substring(0, 5), { saturation: 60, lightness: 55, alpha: 0.6 }) }}
          >
            <span
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: getSlotColor(r.startTime.substring(0, 5)) }}
            />
            {r.startTime.substring(0, 5)}
          </Badge>
          <span className="truncate">
            {r.customerFirstName} {r.customerLastName}
          </span>
          <span className="flex items-center gap-0.5 text-muted-foreground">
            <Users className="w-3 h-3" />
            {r.numberOfPeople}
          </span>
        </div>
      </div>
    </div>
  );
};
